import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Button, Input, Label, Row, Col, Alert, Spinner, UncontrolledTooltip, FormGroup } from "reactstrap";
import classnames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import CommonFileProgress from "Components/Common/CommonFileUpload/CommonFileProgress";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { formatDate, GetUserData, showToastMessage, parseLocalDate } from "common/service/CommonService";
import { AMBULATORY_VISIT_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "../../../../Components/Common/Commonbadge";
import { DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS, BLOCK_REASONS_OPTIONS, BLOOD_COLLECTION_REMARKS_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import { useForm, Controller } from "react-hook-form";
import { useStudyDetailsService } from "hooks/useCommonQuery";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { activityColumns, eligibilityFields, getBlockDateFields, getBlockReasonFields, getBlockTypeFields, getDropoutFields, overallStatusFields, getSubjectAssessmentFields } from "../helper/ambulatoryvisitconstants";
import { validations } from "validations/FormValidations";
import CRFScreeningModal from "modules/Volunteer/AssigningStudyModule/StudyScreening/components/CRFScreeningModal";
import CommonButton from "Components/Common/CommonButton";


interface AmbulatoryFile extends File {
  attachmenttype?: string | null;
  visitactivityid?: number;
}

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};

const AmbulatorySubjectDetail: React.FC = () => {
  document.title = "Ambulatory Subject Detail | ZENTIXS VISTA";

  const navigate = useNavigate();
  const { visitId, subjectId } = useParams<{ visitId: string; subjectId: string }>();
  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
      { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
      { text: "Subject Visit Activity " },
    ]);
    return () => setBreadcrumbItems?.([]);
  }, [setBreadcrumbItems, visitId]);

  const [visit, setVisit] = useState<any>(null);
  const [subject, setSubject] = useState<any>(null);

  const [activeActivityId, setActiveActivityId] = useState<number | null>(null);
  const [activityAttachments, setActivityAttachments] = useState<Record<number, {
    uploadedFiles: AmbulatoryFile[];
    existingAttachments: any[];
    deletedAttachmentIds: number[];
  }>>({});
  const [previewFile, setPreviewFile] = useState<AmbulatoryFile | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [comments, setComments] = useState("");
  const [selectedCRFPreview, setSelectedCRFPreview] = useState<any>(null);
  const [isCRFPreviewModalOpen, setIsCRFPreviewModalOpen] = useState<boolean>(false);
  const [isSection1Completed, setIsSection1Completed] = useState<boolean>(false);
  const [isSection2Completed, setIsSection2Completed] = useState<boolean>(false);

  const {
    control,
    setValue,
    reset,
    watch,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      subjectreported: "yes",
      identificationtime: "09:15 AM",
      breathalcoholresult: "negative",
      breathalcoholcolor: "Green",
      breathalcoholconcentration: "0.00",
      idcardgiven: "yes",
      wellbeing: "well",
      lightcondition: "normal",
      id_card_returned: "yes",
      blood_scheduled_time: "",
      blood_actual_time: "",
      wet_ice_bath: "yes",
      remarks: "",
      description: "",
      recorded_by: "Jane Doe (Clinical Nurse)",
      status: "",
      dropouttype: "",
      aereferenceno: "",
      dropoutdate: "",
      studystage: "",
      effectivedate: "",
      dropoutreason: "",
      detailedreason: "",
      blocktype: "Temporary",
      blockstartdate: "",
      blockenddate: "",
      blockduration: "0 days",
      blockreason: "",
      detailedremarks: "",
      rescreeningeligibility: "",
    },
    mode: "onChange"
  });

  const statusValue = watch("status");
  const dropoutDateValue = watch("dropoutdate");
  const effectiveDateValue = watch("effectivedate");
  const dropoutTypeValue = watch("dropouttype");

  const blockTypeValue = watch("blocktype");
  const blockStartDateValue = watch("blockstartdate");
  const blockEndDateValue = watch("blockenddate");

  useEffect(() => {
    if (statusValue && subject && subject.status !== statusValue) {
      setSubject((prev: any) => ({ ...prev, status: statusValue }));
    }
  }, [statusValue, subject]);

  useEffect(() => {
    if (dropoutDateValue && effectiveDateValue) {
      const start = new Date(dropoutDateValue);
      const end = new Date(effectiveDateValue);
      if (end < start) {
        setValue("effectivedate", "", { shouldValidate: true });
      }
    }
  }, [dropoutDateValue, effectiveDateValue, setValue]);

  useEffect(() => {
    if (blockTypeValue === undefined || blockTypeValue === null) {
      setValue("blocktype", "Temporary")
    }
    if (blockTypeValue === "Permanent") {
      setValue("blockduration", "0");
      return;
    }

    if (!blockStartDateValue) {
      setValue("blockenddate", "", { shouldValidate: true });
      setValue("blockduration", "0 days");
      return;
    }

    if (blockStartDateValue && blockEndDateValue) {
      const start = new Date(blockStartDateValue);
      const end = new Date(blockEndDateValue);
      if (end < start) {
        setValue("blockenddate", "", { shouldValidate: true });
        setValue("blockduration", "0 days");
        return;
      }
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setValue("blockduration", diffDays > 0 ? `${diffDays} days` : "0 days");
    } else {
      setValue("blockduration", "0 days");
    }
  }, [blockStartDateValue, blockEndDateValue, blockTypeValue, setValue]);

  useEffect(() => {
    if (dropoutDateValue || isSubmitted) {
      trigger("dropoutdate");
    }
  }, [dropoutDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (effectiveDateValue || isSubmitted) {
      trigger("effectivedate");
    }
  }, [effectiveDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (blockStartDateValue || isSubmitted) {
      trigger("blockstartdate");
    }
  }, [blockStartDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (blockEndDateValue || isSubmitted) {
      trigger("blockenddate");
    }
  }, [blockEndDateValue, isSubmitted, trigger]);

  useEffect(() => {
    loadCodelistsDetailId([
      "DROPOUT_TYPE_OPTIONS",
      "DROPOUT_REASONS_OPTIONS",
      "BLOCK_REASONS_OPTIONS",
      "BLOOD_COLLECTION_REMARKS_OPTIONS"
    ]).then((result) => {
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdown options failed to load");
      }
    });
  }, []);
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteExisting = (activityId: number, attachmentId: number) => {
    setActivityAttachments((prev) => {
      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          deletedAttachmentIds: [...current.deletedAttachmentIds, attachmentId],
          existingAttachments: current.existingAttachments.filter(
            (a) => Number(a.ambulatoryattachmentid || a.id) !== Number(attachmentId)
          ),
        },
      };
    });
  };

  const handlePreview = (file: any) => {
  

    const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, activityId: number) => {
    const files = (Array.from(e.target.files || []) as AmbulatoryFile[]).map(f => {
      f.visitactivityid = activityId;
      return f;
    });
    if (files.length === 0) return;

    setActivityAttachments((prev) => {
      const totalExistingSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.existingAttachments?.reduce((s, f) => s + parseSizeToBytes(f.filesize || f.size || 0), 0) || 0);
      }, 0);
      const totalUploadedSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.uploadedFiles?.reduce((s, f) => s + f.size, 0) || 0);
      }, 0);
      const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
      const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

      if (totalExistingSize + totalUploadedSize + incomingTotal > MAX_TOTAL_SIZE) {
        showToastMessage("error", "Total attachment size cannot exceed 20 MB.");
        return prev;
      }

      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: [...current.uploadedFiles, ...files],
        },
      };
    });
    setActiveActivityId(null);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent, activityId: number) => {
    e.preventDefault();
    setIsDragging(false);
    const files = (Array.from(e.dataTransfer.files) as AmbulatoryFile[]).map(f => {
      f.visitactivityid = activityId;
      return f;
    });
    if (files.length === 0) return;

    setActivityAttachments((prev) => {
      const totalExistingSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.existingAttachments?.reduce((s, f) => s + parseSizeToBytes(f.filesize || f.size || 0), 0) || 0);
      }, 0);
      const totalUploadedSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.uploadedFiles?.reduce((s, f) => s + f.size, 0) || 0);
      }, 0);
      const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
      const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

      if (totalExistingSize + totalUploadedSize + incomingTotal > MAX_TOTAL_SIZE) {
        showToastMessage("error", "Total attachment size cannot exceed 20 MB.");
        return prev;
      }

      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: [...current.uploadedFiles, ...files],
        },
      };
    });
    setActiveActivityId(null);
  };

  const removeFile = (index: number, activityId: number) => {
    setActivityAttachments((prev) => {
      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: current.uploadedFiles.filter((_, i) => i !== index),
        },
      };
    });
  };



  const {
    SelectdataAmbulatoryVisit,
    SelectAllAssignedSubjects,
    updateSubjectDecision,
    updateSubjectCRFResult,
    insertAmbulatoryVisitSubjectAssessment,
    SelectAmbulatoryVisitSubjectAssessment,
    insertAmbulatoryVisitSubjectBloodCollection,
    SelectAmbulatoryVisitSubjectBloodCollection,
  } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(visitId),
    subjectassignmentid: subject?.visitsubjectassignmentid ? Number(subject.visitsubjectassignmentid) : subject?.id ? Number(subject.id) : undefined,
    subjectassessmentid: subject?.subjectassessmentid ? Number(subject.subjectassessmentid) : undefined,
  });

  useEffect(() => {
    if (SelectAmbulatoryVisitSubjectAssessment?.data) {
      const resData = (SelectAmbulatoryVisitSubjectAssessment.data as any)?.data;
      const assessment = Array.isArray(resData) ? resData[0] : resData;
      if (assessment && (assessment.subjectassessmentid || assessment.subjectreported !== undefined)) {
        setIsSection1Completed(true);
        if (assessment.subjectassessmentid) {
          setSubject((prev: any) => ({ ...prev, subjectassessmentid: assessment.subjectassessmentid }));
        }
        if (assessment.subjectreported !== undefined && assessment.subjectreported !== null) {
          setValue("subjectreported", assessment.subjectreported === true || String(assessment.subjectreported).toLowerCase() === "yes" ? "yes" : "no");
        }
        if (assessment.idcardgiven !== undefined && assessment.idcardgiven !== null) {
          setValue("idcardgiven", assessment.idcardgiven === true || String(assessment.idcardgiven).toLowerCase() === "yes" ? "yes" : "no");
        }
        if (assessment.breathalcoholresult) {
          setValue("breathalcoholresult", String(assessment.breathalcoholresult).toLowerCase());
        }
        if (assessment.breathalcoholcolor) {
          setValue("breathalcoholcolor", assessment.breathalcoholcolor);
        }
        if (assessment.breathalcoholconcentration !== undefined && assessment.breathalcoholconcentration !== null) {
          setValue("breathalcoholconcentration", String(assessment.breathalcoholconcentration));
        }
        if (assessment.identificationtime) {
          const rawTime = String(assessment.identificationtime);
          const timeStr = rawTime.includes("T") ? rawTime.split("T")[1].substring(0, 5) : rawTime.substring(0, 5);
          setValue("identificationtime", timeStr);
        }
      }
    }
  }, [SelectAmbulatoryVisitSubjectAssessment?.data, setValue]);

  useEffect(() => {
    if (SelectAmbulatoryVisitSubjectBloodCollection?.data) {
      const resData = (SelectAmbulatoryVisitSubjectBloodCollection.data as any)?.data;
      const collection = Array.isArray(resData) ? resData[0] : resData;
      if (collection && (collection.subjectbloodcollectionid || collection.bloodcollectionstatus)) {
        setIsSection2Completed(true);
        if (collection.subjectbloodcollectionid) {
          setSubject((prev: any) => ({ ...prev, subjectbloodcollectionid: collection.subjectbloodcollectionid }));
        }
        if (collection.wellbeing !== undefined && collection.wellbeing !== null) {
          setValue("wellbeing", collection.wellbeing === true || String(collection.wellbeing).toLowerCase() === "well" ? "well" : "unwell");
        }
        if (collection.lightcondition !== undefined && collection.lightcondition !== null) {
          setValue("lightcondition", collection.lightcondition === true || String(collection.lightcondition).toLowerCase() === "normal" ? "normal" : "sodium");
        }
        if (collection.idcardreturned !== undefined && collection.idcardreturned !== null) {
          setValue("id_card_returned", collection.idcardreturned === true || String(collection.idcardreturned).toLowerCase() === "yes" ? "yes" : "no");
        }
        if (collection.keptinicebath !== undefined && collection.keptinicebath !== null) {
          setValue("wet_ice_bath", collection.keptinicebath === true || String(collection.keptinicebath).toLowerCase() === "yes" ? "yes" : "no");
        }
        if (collection.remarks !== undefined && collection.remarks !== null) {
          setValue("remarks", Number(collection.remarks) as any);
        }
        if (collection.description) {
          setValue("description", collection.description);
        }
        if (collection.bloodsampleactualtime) {
          const rawTime = String(collection.bloodsampleactualtime);
          const timeStr = rawTime.includes("T") ? rawTime.split("T")[1].substring(0, 5) : rawTime.substring(0, 5);
          setValue("blood_actual_time", timeStr);
        }
      }
    }
  }, [SelectAmbulatoryVisitSubjectBloodCollection?.data, setValue]);

  const handleSaveParticipantScreening = async () => {
    const isValid = await trigger([
      "subjectreported",
      "breathalcoholresult",
      "identificationtime",
    ]);
    if (!isValid) return;

    const formValues: any = getValues();
    const userData = GetUserData() as any;

    let isoTime = formValues.identificationtime;
    if (isoTime && typeof isoTime === "string" && isoTime.includes(":")) {
      const todayStr = new Date().toISOString().split("T")[0];
      isoTime = `${todayStr}T${isoTime}:00`;
    }

    const payload = {
      visitsubjectassignmentid: Number(subject?.visitsubjectassignmentid || subject?.id),
      ambulatoryvisitsamplingpointid: (subject?.ambulatoryvisitsamplingpointid || subject?.samplingpointid) ? Number(subject.ambulatoryvisitsamplingpointid || subject.samplingpointid) : undefined,
      subjectreported: formValues.subjectreported === "yes" || formValues.subjectreported === true,
      identificationtime: isoTime || null,
      breathalcoholresult: formValues.breathalcoholresult ? (String(formValues.breathalcoholresult).toLowerCase() === "positive" ? "Positive" : "Negative") : null,
      breathalcoholcolor: formValues.breathalcoholcolor || null,
      breathalcoholconcentration: formValues.breathalcoholconcentration !== undefined && formValues.breathalcoholconcentration !== "" ? parseFloat(formValues.breathalcoholconcentration) : null,
      idcardgiven: formValues.idcardgiven === "yes" || formValues.idcardgiven === true,
      subjectassessmentstatus: "completed",
      createdby: userData?.userid ? Number(userData.userid) : undefined,
    };

    insertAmbulatoryVisitSubjectAssessment.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        setIsSection1Completed(true);
        const newAssessmentId = res?.data?.subjectassessmentid || res?.data?.[0]?.subjectassessmentid || res?.subjectassessmentid;
        if (newAssessmentId) {
          setSubject((prev: any) => ({ ...prev, subjectassessmentid: newAssessmentId }));
        }
        SelectAmbulatoryVisitSubjectAssessment?.refetch?.();
        SelectAllAssignedSubjects?.refetch?.();
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err);
      },
    });
  };

  const handleSaveSampleCollection = async () => {
    const isValid = await trigger(["wellbeing", "blood_actual_time"]);
    if (!isValid) return;

    if (!subject?.subjectassessmentid) {
      showToastMessage("error", "Please save Subject Assessment Form");
      return;
    }

    const formValues: any = getValues();
    const userData = GetUserData() as any;

    let isoTime = formValues.blood_actual_time;
    if (isoTime && typeof isoTime === "string" && isoTime.includes(":")) {
      const todayStr = new Date().toISOString().split("T")[0];
      isoTime = `${todayStr}T${isoTime}:00`;
    }

    const payload = {
      createdby: userData?.userid ? Number(userData.userid) : 11,
      subjectassessmentid: Number(subject.subjectassessmentid),
      visitsubjectassignmentid: Number(subject.visitsubjectassignmentid || subject.id),
      ambulatoryvisitsamplingpointid: (subject.ambulatoryvisitsamplingpointid || subject.samplingpointid) ? Number(subject.ambulatoryvisitsamplingpointid || subject.samplingpointid) : undefined,
      wellbeing: formValues.wellbeing === "well" || formValues.wellbeing === true,
      lightcondition: formValues.lightcondition === "normal" || formValues.lightcondition === true,
      bloodsampleactualtime: isoTime || null,
      idcardreturned: formValues.id_card_returned === "yes" || formValues.id_card_returned === true,
      keptinicebath: formValues.wet_ice_bath === "yes" || formValues.wet_ice_bath === true,
      remarks: formValues.remarks ? Number(formValues.remarks) : null,
      description: formValues.description || "Blood sample drawn smoothly without issues",
      bloodcollectionstatus: "completed",
      ...(subject.subjectbloodcollectionid ? { subjectbloodcollectionid: Number(subject.subjectbloodcollectionid) } : {}),
    };

    insertAmbulatoryVisitSubjectBloodCollection.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        setIsSection2Completed(true);
        const newCollectionId = res?.data?.subjectbloodcollectionid || res?.data?.[0]?.subjectbloodcollectionid || res?.subjectbloodcollectionid;
        if (newCollectionId) {
          setSubject((prev: any) => ({ ...prev, subjectbloodcollectionid: newCollectionId }));
        }
        SelectAmbulatoryVisitSubjectBloodCollection?.refetch?.();
        SelectAllAssignedSubjects?.refetch?.();
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err);
      },
    });
  };

  const visitResData = (SelectdataAmbulatoryVisit.data as any)?.data;
  const allVisitCrfs: any[] = visitResData?.visitcrf || [];
  const subjectCrfList = allVisitCrfs.filter((item: any) => {
    if (!item.subjectno) return true;
    const targetSub = String(subjectId || subject?.subjectId || subject?.subjectno || "").trim();
    return String(item.subjectno).trim() === targetSub;
  });

  const { SelectQuery: StudySelectQuery } = useStudyDetailsService(
    undefined,
    undefined,
    studyid || undefined
  );
  const studyDetails = StudySelectQuery?.data?.data?.[0];

  const getRomanNumeral = (num: number): string => {
    const romanMap: { [key: number]: string } = {
      1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI", 7: "VII", 8: "VIII", 9: "IX", 10: "X"
    };
    return romanMap[num] || String(num);
  };

  const periodsList = studyDetails?.periods || [];
  const studyStageOptions = periodsList.map((p: any) => ({
    label: `Period ${getRomanNumeral(Number(p.period_no))}`,
    value: `Period ${getRomanNumeral(Number(p.period_no))}`,
  }));

  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data) {
      const visitRes = (SelectdataAmbulatoryVisit.data as any)?.data;
      const visitData = visitRes?.visit?.[0];
      setVisit({
        id: visitData?.visitid || visitId,
        visitNumber: visitData?.visitnumber || `Visit ${String(visitData?.visitid || visitId).padStart(2, "0")}`,
        visitName: visitData?.visitname || "--",
        scheduledDate: visitData?.scheduleddate ? new Date(visitData.scheduleddate).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : "30 Jun 2026",
        visitStatus: visitData?.visitstatus || "scheduled",
      });

      if (SelectAllAssignedSubjects.data) {
        const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
        const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
        if (foundSub) {
          const mappedActivities = (foundSub.activities || visitRes?.activities || []).map((act: any) => {
            const actId = act.activityid || act.id;
            const masterAct = (visitRes?.activities || []).find(
              (m: any) => Number(m.activityid || m.id) === Number(actId)
            );
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              masterAct?.ismandatory === true || masterAct?.ismandatory === 1 || String(masterAct?.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true" ||
              masterAct?.mandatory === true || masterAct?.mandatory === 1 || String(masterAct?.mandatory).toLowerCase() === "true";

            return {
              id: actId,
              name: act.activityname || act.name,
              status: act.status || "Not Started",
              result: act.result || null,
              performedDate: act.performedDate || act.completedon || "",
              performedTime: act.performedTime || "",
              performedBy: act.performedBy || "",
              remarks: act.remarks || "",
              ismandatory: isMandatory
            };
          });

          const spList = visitRes?.samplingpoints || visitRes?.["samplingpoints "] || visitRes?.sampling_points || visitData?.samplingpoints || [];
          const spItem = Array.isArray(spList) ? spList[0] : spList;
          const resolvedSpId = typeof spItem === "object" ? (spItem?.ambulatoryvisitsamplingpointid || spItem?.samplingpointid || spItem?.id) : spItem;

          setSubject({
            ...foundSub,
            ambulatoryvisitsamplingpointid: foundSub.ambulatoryvisitsamplingpointid || foundSub.samplingpointid || visitData?.ambulatoryvisitsamplingpointid || visitData?.samplingpointid || visitRes?.samplingpointid || resolvedSpId,
            subjectId: foundSub.subjectno || subjectId,
            initials: foundSub.initials || foundSub.subjectname?.slice(0, 2).toUpperCase() || "SJ",
            checkinTime: foundSub.checkintime || foundSub.checkinTime || "09:00 AM",
            status: foundSub.subjectvisitstatus || foundSub.status || "checked in",
            activities: mappedActivities
          });

          const attachmentsMap: Record<number, { uploadedFiles: AmbulatoryFile[], existingAttachments: any[], deletedAttachmentIds: number[] }> = {};
          mappedActivities.forEach((act: any) => {
            attachmentsMap[act.id] = {
              uploadedFiles: [],
              existingAttachments: act.attachments || [],
              deletedAttachmentIds: []
            };
          });
          if (foundSub.attachments && Array.isArray(foundSub.attachments)) {
            foundSub.attachments.forEach((att: any) => {
              const actId = Number(att.activityid || att.activityId);
              if (actId && attachmentsMap[actId]) {
                if (!attachmentsMap[actId].existingAttachments.some(e => e.ambulatoryattachmentid === att.ambulatoryattachmentid)) {
                  attachmentsMap[actId].existingAttachments.push(att);
                }
              }
            });
          }
          setActivityAttachments(attachmentsMap);
          setComments(foundSub.comments || foundSub.remarks || "");
          const rawStatus = (foundSub.subjectvisitstatus || foundSub.status || "").toLowerCase();
          const initStatus = ["completed", "dropped", "blocked"].includes(rawStatus) ? rawStatus : "";
          reset({
            status: initStatus,
            dropouttype: foundSub.dropouttype || "",
            aereferenceno: foundSub.adverseeventnumber || foundSub.aereferenceno || "",
            dropoutdate: foundSub.dropoutdate || "",
            studystage: foundSub.studystage || "",
            effectivedate: foundSub.effectivedate || "",
            dropoutreason: foundSub.dropoutreason || "",
            detailedreason: foundSub.detailedreason || foundSub.remarks || "",
            blockstartdate: foundSub.blockstartdate || foundSub.startdate || "",
            blockreason: foundSub.blockreason || "",
            detailedremarks: foundSub.detailedremarks || foundSub.remarks || "",
          });
        } else {
          const mappedActivities = (visitRes?.activities || []).map((act: any) => {
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true";
            return {
              id: act.activityid || act.id,
              name: act.activityname || act.name,
              status: "Not Started",
              result: null,
              performedDate: "",
              performedTime: "",
              performedBy: "",
              remarks: "",
              ismandatory: isMandatory
            };
          });
          const spListFallback = visitRes?.samplingpoints || visitRes?.["samplingpoints "] || visitRes?.sampling_points || visitData?.samplingpoints || [];
          const spItemFallback = Array.isArray(spListFallback) ? spListFallback[0] : spListFallback;
          const resolvedSpIdFallback = typeof spItemFallback === "object" ? (spItemFallback?.ambulatoryvisitsamplingpointid || spItemFallback?.samplingpointid || spItemFallback?.id) : spItemFallback;

          setSubject({
            subjectId: subjectId,
            ambulatoryvisitsamplingpointid: visitData?.ambulatoryvisitsamplingpointid || visitData?.samplingpointid || visitRes?.samplingpointid || resolvedSpIdFallback,
            initials: "SJ",
            checkinTime: "09:00 AM",
            status: "checked in",
            activities: mappedActivities
          });
          const attachmentsMap: Record<number, { uploadedFiles: AmbulatoryFile[], existingAttachments: any[], deletedAttachmentIds: number[] }> = {};
          mappedActivities.forEach((act: any) => {
            attachmentsMap[act.id] = {
              uploadedFiles: [],
              existingAttachments: [],
              deletedAttachmentIds: []
            };
          });
          setActivityAttachments(attachmentsMap);
          setComments("");
          reset({
            status: "",
            dropouttype: "",
            aereferenceno: "",
            dropoutdate: "",
            studystage: "",
            effectivedate: "",
            dropoutreason: "",
            detailedreason: "",
            blockstartdate: "",
            blockreason: "",
            detailedremarks: "",
          });
        }
      }
    }
  }, [SelectdataAmbulatoryVisit.data, SelectAllAssignedSubjects.data, subjectId, visitId, reset]);

  const saveState = (updatedSubject: any) => {
    setSubject(updatedSubject);
  };

  const handleActivityFieldChange = (actId: number, field: string, value: any) => {
    if (!subject) return;
    const updatedActivities = subject.activities.map((act: any) => {
      if (act.id === actId) {
        return {
          ...act,
          [field]: value
        };
      }
      return act;
    });
    saveState({ ...subject, activities: updatedActivities });
  };

  const handleActivityResultChange = (actId: number, resultType: "Passed" | "Failed") => {
    if (!subject) return;
    const userData = GetUserData();
    const userName = userData ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim() || userData.username || "Riya Patel" : "Riya Patel";

    const updatedActivities = subject.activities.map((act: any) => {
      if (act.id === actId) {
        const newResult = act.result === resultType ? null : resultType;
        const now = new Date();
        const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        const dateStr = now.toISOString().split('T')[0];

        return {
          ...act,
          result: newResult,
          status: newResult ? "completed" : "Not Started",
          performedDate: newResult ? (act.performedDate || dateStr) : "",
          performedTime: newResult ? (act.performedTime || timeStr) : "",
          performedBy: newResult ? (act.performedBy || userName) : ""
        };
      }
      return act;
    });
    saveState({ ...subject, activities: updatedActivities });
  };

  const handleSaveCRFAnswers = (answers: any) => {
    if (!selectedCRFPreview?.subjectassignmentresultid) {
      showToastMessage("error", "Invalid subject assignment result ID");
      return;
    }

    const payload = {
      subjectassignmentresultid: selectedCRFPreview.subjectassignmentresultid,
      crfdetails: answers,
    };

    updateSubjectCRFResult.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        setIsCRFPreviewModalOpen(false);
        setSelectedCRFPreview(null);
        SelectdataAmbulatoryVisit.refetch();
      },
      onError: (err: any) => {
        showToastMessage("error", err);
      },
    });
  };

  const handleSubmitSubjectDetail = async () => {
    // Validate custom activity remarks length
    const hasInvalidRemarks = subject?.activities?.some((act: any) => {
      const validationResult = validations.ambulatoryVisit.remarks.validate(act.remarks || "");
      return typeof validationResult === "string";
    });

    const isValid = await trigger();
    if (!isValid) {
      setTimeout(() => {
        const errorEl = document.querySelector(".is-invalid, .invalid-feedback, .form-feedback");
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    const formValues = getValues();

    const payload = {
      subject,
      formValues,
      comments,
      studyid,
      subjectId,
    };

    updateSubjectDecision.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Overall decision saved successfully!");
        navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""));
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to save overall decision!");
      },
    });
  };

  if (
    SelectdataAmbulatoryVisit.isLoading ||
    SelectdataAmbulatoryVisit.isFetching ||
    SelectAllAssignedSubjects.isLoading ||
    SelectAllAssignedSubjects.isFetching ||
    StudySelectQuery?.isLoading ||
    StudySelectQuery?.isFetching ||
    !subject ||
    !visit
  ) {
    return <FullPageLoader message="Loading Subject Details..." />;
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>

        {/* Subject Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and active visit check-in status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{subject.subjectId}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-passport-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Number</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{visit.visitNumber}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-bookmark-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Name</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      <TruncatedText text={visit.visitName || "--"} maxWidth="150px" />
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>{formatDate(visit.scheduledDate)}</div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-time-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Check-In</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {subject.checkintime ? subject.checkintime.split(" ")[0] : "09:00 AM"}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Visit Status</div>
                    <Commonbadge status={subject?.subjctvisitstatus || "checked in"} style={{ width: "90px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Column Layout / Content Panels */}
        <div className="d-flex flex-column">

          {/* Configured CRFs Section */}
          {subjectCrfList && subjectCrfList.length > 0 && (
            <SectionCard
              icon="ri-article-line"
              title="Configured CRFs"
              subtitle="View and complete form details for configured visit CRFs."
            >
              <div className="p-3 border rounded-3 bg-white">
                <div className="text-muted fs-13 d-flex align-items-center gap-1 mb-2">
                  <i className="ri-information-line fs-15" />
                  <span>Click on CRF to fill or view form</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {subjectCrfList.map((crfItem: any, idx: number) => {
                    const title = crfItem.crfquestiondetails?.title || crfItem.crfname || crfItem.name || `CRF #${crfItem.subjectassignmentresultid || idx + 1}`;
                    const answers = crfItem.crfanswerdetails;
                    const st = (crfItem.crfstatus || "").toLowerCase().trim();
                    const isFilled = st === "completed";
                    return (
                      <button
                        key={crfItem.subjectassignmentresultid || crfItem.visitcrfid || idx}
                        type="button"
                        onClick={() => {
                          const schema = crfItem.crfquestiondetails || crfItem.crfdetails || crfItem.json || {};
                          const crfFormObj = {
                            id: crfItem.subjectassignmentresultid || crfItem.visitcrfid || idx,
                            subjectassignmentresultid: crfItem.subjectassignmentresultid,
                            name: title,
                            crfid: crfItem.crfid || crfItem.id,
                            crfdetails: {
                              ...schema,
                              answers: answers || null,
                            },
                            crfanswerdetails: answers || null,
                            selectedCrfDetails: {
                              crfdetails: {
                                ...schema,
                                answers: answers || null,
                              },
                              title: title,
                              answers: answers || null,
                            },
                          };
                          setSelectedCRFPreview(crfFormObj);
                          setIsCRFPreviewModalOpen(true);
                        }}
                        className="btn btn-sm rounded-pill px-3 py-2 fs-13 border-0 shadow-none d-inline-flex align-items-center cursor-pointer"
                        style={{
                          backgroundColor: isFilled ? "#eef9f2" : "#e0f2fe",
                          color: "#1f2937",
                          transition: "all 0.15s ease-in-out",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isFilled ? "#e2f4e8" : "#d3e7f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isFilled ? "#eef9f2" : "#e0f2fe";
                        }}
                      >
                        <i className={`ri-file-text-line fs-16 me-2 ${isFilled ? "text-success" : "text-primary"}`} />
                        <span className="fw-bold text-dark me-2">{title}</span>
                        <span className={`d-inline-flex align-items-center gap-1 fs-12 fw-semibold ${isFilled ? "text-success" : "text-primary"}`}>
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: isFilled ? "#10b981" : "#1e6b91",
                              display: "inline-block",
                            }}
                          />
                          <span>{isFilled ? "Completed" : "Pending"}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </SectionCard>
          )}

          {/* Participant Screening Card */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Screening"
            subtitle="Subject screening and breath alcohol test details recorded by clinical staff."
            rightContent={
              isSection1Completed ? (
                <span className="badge bg-success-subtle text-success fs-12 px-2.5 py-1.5 rounded-pill border border-success d-inline-flex align-items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Completed
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-12 px-2.5 py-1.5 rounded-pill border border-warning d-inline-flex align-items-center gap-1">
                  <i className="ri-time-line" /> Pending Completion
                </span>
              )
            }
          >
            <div>
              <CommonForm
                fields={getSubjectAssessmentFields(isSection1Completed)}
                control={control}
                errors={errors}
                columns={3}
                setValue={setValue}
              />
            </div>

            <div className="text-end mt-4">
              {isSection1Completed ? (
                <Button color="success" outline size="sm" className="px-4 shadow-sm" disabled style={{ opacity: 0.9 }}>
                  <i className="ri-checkbox-circle-line me-1" /> Screening Completed
                </Button>
              ) : (
                <CommonButton
                  color="primary"
                  size="sm"
                  className="px-4 shadow-sm"
                  onClick={handleSaveParticipantScreening}
                  disabled={Boolean(insertAmbulatoryVisitSubjectAssessment?.isPending)}
                >
                  {insertAmbulatoryVisitSubjectAssessment?.isPending ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" /> Save Subject Screening
                    </>
                  )}
                </CommonButton>
              )}
            </div>
          </SectionCard>

          {/* Sample Collection Card */}
          <SectionCard
            icon="ri-test-tube-line"
            title="Sample Collection"
            subtitle="Blood sample collection timing, ice bath status, and deviation details recorded by phlebotomist."
            rightContent={
              isSection2Completed ? (
                <span className="badge bg-success-subtle text-success fs-12 px-2.5 py-1.5 rounded-pill border border-success d-inline-flex align-items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Completed
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-12 px-2.5 py-1.5 rounded-pill border border-warning d-inline-flex align-items-center gap-1">
                  <i className="ri-time-line" /> Pending Completion
                </span>
              )
            }
          >
            <div className="mb-3">
              <CommonForm
                fields={[
                  { name: "wellbeing", label: "Well Being", type: "radio", required: true, rules: { required: "Well Being is required" }, disabled: isSection2Completed, options: [{ label: "Well", value: "well" }, { label: "Unwell", value: "unwell" }] },
                  { name: "lightcondition", label: "Light Condition", type: "radio", disabled: isSection2Completed, options: [{ label: "Normal", value: "normal" }, { label: "Sodium Vapor", value: "sodium" }] },
                  { name: "id_card_returned", label: "ID Card Returned?", type: "radio", disabled: isSection2Completed, options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] }
                ]}
                control={control}
                errors={errors}
                columns={3}
                setValue={setValue}
              />
            </div>

            <div className="mb-3">
              <CommonForm
                fields={[
                  { name: "blood_scheduled_time", label: "Blood Sample Scheduled Time", type: "time", disabled: true },
                  { name: "blood_actual_time", label: "Blood Sample Actual Time", type: "time", required: true, disabled: isSection2Completed },
                  { name: "wet_ice_bath", label: "Kept in Wet Ice Bath?", type: "radio", disabled: isSection2Completed, options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] }
                ]}
                control={control}
                errors={errors}
                columns={3}
                setValue={setValue}
              />
            </div>

            <div className="mb-3">
              <CommonForm
                fields={[
                  {
                    name: "remarks",
                    label: "Remarks",
                    type: "select",
                    disabled: isSection2Completed,
                    options: BLOOD_COLLECTION_REMARKS_OPTIONS
                  },
                  {
                    name: "description",
                    label: "Additional Remarks",
                    type: "textarea",
                    disabled: isSection2Completed,
                    placeholder: "Enter additional remarks / observations..."
                  }
                ]}
                control={control}
                errors={errors}
                columns={2}
                setValue={setValue}
              />
            </div>

            <div className="text-end mt-4">
              {isSection2Completed ? (
                <CommonButton color="success" outline size="sm" className="px-4 shadow-sm" disabled style={{ opacity: 0.9 }}>
                  <i className="ri-checkbox-circle-line me-1" /> Sample Collection Completed
                </CommonButton>
              ) : (
                <CommonButton
                  color="primary"
                  size="sm"
                  className="px-4 shadow-sm"
                  onClick={handleSaveSampleCollection}
                  disabled={Boolean(insertAmbulatoryVisitSubjectBloodCollection?.isPending)}
                >
                  {insertAmbulatoryVisitSubjectBloodCollection?.isPending ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" /> Save Sample Collection
                    </>
                  )}
                </CommonButton>
              )}
            </div>
          </SectionCard>

          {/* Card 3: Overall Decision */}
          <SectionCard
            icon="ri-shield-check-line"
            title="Overall Decision"
            subtitle="Record the overall decision or action applicable for this subject for this visit."
          >
            <div className="mb-3 pb-1 border-bottom">
              <CommonForm
                fields={overallStatusFields}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />
            </div>

            {/* Dropped (Discontinued) Fields */}
            {(subject.status?.toLowerCase() === "dropped") && (() => {
              const fields = getDropoutFields(dropoutDateValue, dropoutTypeValue, studyStageOptions);
              const selectedType = DROPOUT_TYPE_OPTIONS.find(
                (opt) => String(opt.value) === String(dropoutTypeValue) || opt.label === dropoutTypeValue
              )?.label || (dropoutTypeValue ? String(dropoutTypeValue) : null);
              const isAE = selectedType === "Medical / AE";
              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-logout-box-r-line me-2 text-warning" />
                    Dropout Details
                  </h6>

                  {isAE && (
                    <div className="mt-2 mb-3">
                      <Alert color="danger" className="border-0 bg-light-danger text-danger d-flex flex-column gap-1 p-3">
                        <div className="fw-semibold">
                          <i className="ri-error-warning-line me-2" />
                          Medical / Adverse Event Dropout Selected
                        </div>
                        <div className="small text-muted">
                          Please provide the linked AE reference number for traceability.
                        </div>
                      </Alert>
                    </div>
                  )}

                  <CommonForm
                    fields={isAE ? [fields[0], fields[2], fields[3]] : [fields[0], fields[1], fields[2]]}
                    control={control}
                    errors={errors}
                    columns={3}
                    setValue={setValue}
                  />
                  <div className="mt-3">
                    <CommonForm
                      fields={isAE ? [fields[1], fields[4], fields[5]] : [fields[3], fields[4]]}
                      control={control}
                      errors={errors}
                      columns={3}
                      setValue={setValue}
                    />
                  </div>
                  <div className="mt-3">
                    <CommonForm
                      fields={isAE ? [fields[6]] : [fields[5]]}
                      control={control}
                      errors={errors}
                      columns={1}
                      setValue={setValue}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Blocked Fields */}
            {subject.status?.toLowerCase() === "blocked" && (
              <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                  <i className="ri-forbid-2-line me-2 text-danger" />
                  Block Subject Details
                </h6>
                <div className="mb-3">
                  <CommonForm
                    fields={getBlockTypeFields(BLOCK_REASONS_OPTIONS)}
                    control={control}
                    errors={errors}
                    columns={1}
                    setValue={setValue}
                  />
                </div>

                {blockTypeValue === "Temporary" ? (
                  <>
                    <div className="mb-3">
                      <CommonForm
                        fields={getBlockDateFields(blockTypeValue, blockStartDateValue)}
                        control={control}
                        errors={errors}
                        columns={3}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[0]]} // blockreason
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[1]]} // detailedremarks
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={eligibilityFields}
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <CommonForm
                        fields={[
                          getBlockDateFields(blockTypeValue, blockStartDateValue)[0], // blockstartdate
                          getBlockReasonFields()[0], // blockreason
                        ]}
                        control={control}
                        errors={errors}
                        columns={2}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[1]]} // detailedremarks
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Comments */}
            <Row>
              <Col>
                <label className="form-label text-dark small fw-bold">
                  Additional Comments / Discrepancies
                  <i
                    className="ri-information-line text-muted ms-1 cursor-pointer"
                    id="comments-tooltip"
                    style={{ cursor: "pointer" }}
                  />
                  <UncontrolledTooltip target="comments-tooltip" placement="top">
                    Additional Comments / Discrepancies (max 255 characters)
                  </UncontrolledTooltip>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter any notes, observations, or discrepancy details..."
                  value={comments}
                  maxLength={255}
                  onChange={(e) => setComments(e.target.value)}
                  style={{ fontSize: 13, resize: "none", textAlign: "justify" }}
                />
                <small className="text-muted d-block mt-1 text-end">
                  {comments.length} / 255 characters
                </small>
              </Col>
            </Row>
          </SectionCard>

        </div>
      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
          <div className={formStyles.footerActions}>
            <Button
              type="button"
              color="dark"
              outline
              onClick={() => navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""))
              }
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={updateSubjectDecision.isPending}
              onClick={handleSubmitSubjectDetail}
            >
              {updateSubjectDecision.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" /> Submit
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>
      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        fileUrl={previewFileUrl}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* CRF Screening / Preview Modal */}
      {selectedCRFPreview && (
        <CRFScreeningModal
          isOpen={isCRFPreviewModalOpen}
          toggle={() => {
            setIsCRFPreviewModalOpen(false);
            setSelectedCRFPreview(null);
          }}
          selectedCrf={selectedCRFPreview}
          readonly={false}
          onSave={handleSaveCRFAnswers}
          isPending={updateSubjectCRFResult.isPending}
          onClose={() => {
            setIsCRFPreviewModalOpen(false);
            setSelectedCRFPreview(null);
          }}
          parentBreadcrumbs={[
            { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
            { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
            {
              text: "Subject Visit Activity", onclick: () => {
                setIsCRFPreviewModalOpen(false);
                setSelectedCRFPreview(null);
              }
            },
          ]}
        />
      )}
    </div>
  );
};

export default AmbulatorySubjectDetail;
